import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useGoogleLogin } from "@react-oauth/google";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { closeModal } from "../redux/slices/uiSlice";
import { setUser } from "../redux/slices/userSlice";
import { socialLoginAPI, setAuthToken } from "../api/auth";
import useToast from "./useToast";

interface UseGoogleSocialLoginReturn {
  googleLogin: () => void;
  isLoading: boolean;
  isPending: boolean;
}

const useGoogleSocialLogin = (): UseGoogleSocialLoginReturn => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  // Social Login Mutation
  const { mutate: socialLoginMutate, isPending } = useMutation({
    mutationFn: socialLoginAPI,
    onSuccess: (data) => {

      // Store token in localStorage
      if (data?.data?.token) {
        setAuthToken(data?.data?.token, true);
        localStorage.setItem("token", data?.data?.token || "");
      }

      // Store user data in Redux
      if (data?.data?.user) {
        dispatch(setUser(data.data.user as any));
      }

      useToast(t("messages.loginSuccess"), "success");
      dispatch(closeModal());
      navigate("/create-game");
    },
    onError: (error: Error) => {
      useToast(error.message || t("messages.loginFailed"), "error");
    },
  });

  // Google Login using @react-oauth/google
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setIsGoogleLoading(true);
        const { data } = await axios.get("https://www.googleapis.com/oauth2/v3/userinfo", {
          headers: {
            Authorization: `Bearer ${tokenResponse.access_token}`,
          },
        });


        socialLoginMutate({
          email: data.email,
          social_type: "google",
          social_id: data.sub,
          full_name: data.name || "",
        });
      } catch (error) {
        useToast(t("messages.loginFailed"), "error");
      } finally {
        setIsGoogleLoading(false);
      }
    },
    onError: (error) => {
      useToast(t("messages.loginFailed"), "error");
    },
  });

  return {
    googleLogin: () => googleLogin(),
    isLoading: isGoogleLoading || isPending,
    isPending,
  };
};

export default useGoogleSocialLogin;

